import type {
  AttitudeScore,
  FundamentalCatalogItem,
  GrowthWindow,
  PersonalResponse,
} from '@/api/types';

/**
 * Comportamiento más fuerte de la persona, con el contexto necesario para celebrarlo.
 * Se reconoce a este nivel y no al del fundamental porque nombra una conducta concreta:
 * "Aprendiz" describe algo que se hizo, "Actitudes y Comportamientos" no.
 */
export interface TopAttitude {
  id: string;
  /** Nombre ya localizado por el backend. */
  name: string;
  score: number;
  evaluations: number;
  /** Código estable del catálogo (learner, player_owner…); da la frase de celebración. */
  code?: string;
  /** Color heredado del fundamental al que pertenece. */
  color?: string;
  fundamentalId: string;
}

/** Mínimo de opiniones para celebrar un comportamiento sin sobreinterpretar el dato. */
const MIN_EVALUATIONS_TO_CELEBRATE = 3;

interface AttitudeWithParent {
  attitude: AttitudeScore;
  fundamentalId: string;
}

function collectAttitudes(personalData: PersonalResponse): AttitudeWithParent[] {
  return personalData.fundamentalScores.flatMap(fundamental =>
    (fundamental.attitudes ?? []).map(attitude => ({
      attitude,
      fundamentalId: fundamental.id,
    })),
  );
}

/**
 * Devuelve el comportamiento mejor puntuado con respaldo suficiente, o null cuando
 * ninguno lo tiene (por ejemplo si el catálogo no define el nivel intermedio).
 */
export function resolveTopAttitude(
  personalData: PersonalResponse | undefined,
  catalogData: readonly FundamentalCatalogItem[],
): TopAttitude | null {
  if (!personalData) return null;

  // El respaldo se mide en personas, no en puntajes de daily habit: un comportamiento
  // con 3 hábitos y una sola respuesta acumulaba evaluations = 3 y pasaba el umbral.
  const candidates = collectAttitudes(personalData)
    .filter(entry => entry.attitude.responses >= MIN_EVALUATIONS_TO_CELEBRATE)
    .sort((first, second) => second.attitude.score - first.attitude.score);

  const best = candidates[0];
  if (!best) return null;

  const parentFundamental = catalogData.find(item => item.id === best.fundamentalId);
  const catalogAttitude = parentFundamental?.attitudes.find(item => item.id === best.attitude.id);

  return {
    id: best.attitude.id,
    name: best.attitude.name,
    score: best.attitude.score,
    evaluations: best.attitude.evaluations,
    code: catalogAttitude?.code,
    color: parentFundamental?.color,
    fundamentalId: best.fundamentalId,
  };
}

/**
 * Clave i18n de la frase de celebración del comportamiento. Cada código del catálogo
 * tiene su propia frase traducida; si aparece uno nuevo se usa la genérica.
 */
export function celebrationPhraseKey(code: string | undefined): string {
  return code ? `safe.celebration.${code}` : 'safe.celebration.default';
}

export interface CelebrationDeltaInput {
  /** Comportamiento celebrado, o null cuando se recae en el fundamental. */
  topAttitude: TopAttitude | null;
  /** Fundamental celebrado en el fallback (cuando no hay `topAttitude`). */
  topFundamentalId: string | undefined;
  todayWindow: GrowthWindow | undefined;
  pastWindow: GrowthWindow | undefined;
}

/**
 * Delta del propio nivel celebrado —comportamiento, o fundamental en su ausencia—
 * entre la ventana "hoy" y la ventana "hace 3 meses". Antes se restaba el acumulado de
 * toda la vida (`topFund`, de `accumulatedSorted`) menos el promedio de un periodo
 * (`pastFundAvgs`): dos magnitudes distintas cuya resta no es una tendencia. Además el
 * resultado se pintaba junto al nombre y puntaje del comportamiento top, aunque el
 * delta perteneciera al fundamental. Devuelve null —nunca un cero inventado— cuando el
 * nivel celebrado no aparece en alguna de las dos ventanas.
 */
export function resolveCelebrationDelta({
  topAttitude,
  topFundamentalId,
  todayWindow,
  pastWindow,
}: CelebrationDeltaInput): number | null {
  if (topAttitude) {
    const todayAvg = todayWindow?.attitudes.find(entry => entry.attitudeId === topAttitude.id)?.average;
    const pastAvg = pastWindow?.attitudes.find(entry => entry.attitudeId === topAttitude.id)?.average;
    return todayAvg !== undefined && pastAvg !== undefined ? todayAvg - pastAvg : null;
  }
  if (!topFundamentalId) return null;
  const todayAvg = todayWindow?.fundamentals.find(entry => entry.fundamentalId === topFundamentalId)?.average;
  const pastAvg = pastWindow?.fundamentals.find(entry => entry.fundamentalId === topFundamentalId)?.average;
  return todayAvg !== undefined && pastAvg !== undefined ? todayAvg - pastAvg : null;
}
